import { useState, useMemo, useRef, useCallback } from 'react';
import { Search, MessageCircle, Loader2, Image, Video, Mic, CircleDot, Sticker, FileIcon, MapPin, User, BarChart3, Music } from 'lucide-react';
import { MastersClubIcon } from '@/components/icons/masters-club-icon';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { TelegramChat, SessionStatus, ChatType } from '../types';
import { ChatAvatar } from './ChatAvatar';
import { AccountSwitcher } from './AccountSwitcher';
import { ChatFilters } from './ChatFilters';

// Порог в пикселях для триггера загрузки (когда до низа осталось меньше этого значения)
const LOAD_MORE_THRESHOLD = 200;

interface ChatListProps {
  chats: TelegramChat[];
  isLoading: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  selectedChatId: number | null;
  onSelectChat: (chat: TelegramChat) => void;
  onLoadMore?: () => Promise<boolean>;
  // Multi-account support
  session?: SessionStatus | null;
  accounts?: SessionStatus[];
  isAccountLoading?: boolean;
  onSwitchAccount?: (sessionId: number) => Promise<boolean>;
  onRemoveAccount?: (sessionId: number) => Promise<boolean>;
  onAddAccount?: () => void;
}

export function ChatList({
  chats,
  isLoading,
  isLoadingMore = false,
  hasMore = true,
  selectedChatId,
  onSelectChat,
  onLoadMore,
  session,
  accounts = [],
  isAccountLoading,
  onSwitchAccount,
  onRemoveAccount,
  onAddAccount,
}: ChatListProps) {
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<{
    chatTypes?: ChatType[];
    unreadOnly?: boolean;
    todayOnly?: boolean;
  }>({
    chatTypes: ['user'],
  });
  const scrollRef = useRef<HTMLDivElement>(null);

  // Обработчик скролла для infinite scroll
  const handleScroll = useCallback(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl || isLoadingMore || !hasMore || !onLoadMore) {return;}

    // Если доскроллили близко к низу — загружаем ещё
    const scrollBottom = scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight;
    if (scrollBottom < LOAD_MORE_THRESHOLD) {
      onLoadMore();
    }
  }, [isLoadingMore, hasMore, onLoadMore]);

  // Проверка, является ли сообщение сегодняшним
  const isToday = useCallback((date: Date | string | null | undefined): boolean => {
    if (!date) {return false;}
    const d = date instanceof Date ? date : new Date(date);
    const now = new Date();
    return d.toDateString() === now.toDateString();
  }, []);

  // Фильтрация чатов по поиску, типу, непрочитанности и дате
  const filteredChats = useMemo(() => {
    let result = chats;

    // Фильтр по поиску
    if (search) {
      result = result.filter(chat =>
        (chat.title || '').toLowerCase().includes(search.toLowerCase()) ||
        chat.username?.toLowerCase().includes(search.toLowerCase())
      );
    }

    // Фильтр по типу чата
    if (filters.chatTypes && filters.chatTypes.length > 0) {
      result = result.filter(chat => filters.chatTypes?.includes(chat.chatType));
    }

    // Фильтр по непрочитанным
    if (filters.unreadOnly) {
      result = result.filter(chat => chat.unreadCount > 0);
    }

    // Фильтр по сегодняшним сообщениям
    if (filters.todayOnly) {
      result = result.filter(chat => isToday(chat.lastMessageDate));
    }

    return result;
  }, [chats, search, filters, isToday]);

  const formatTime = (date: Date | string | null | undefined) => {
    if (!date) {return '';}
    const d = new Date(date);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();

    if (isToday) {
      return d.toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString('ru', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="flex flex-col h-full border-r overflow-hidden">
      {/* Account switcher header */}
      {session && onSwitchAccount && onRemoveAccount && onAddAccount && (
        <div className="border-b bg-muted/30 flex-shrink-0">
          <AccountSwitcher
            currentSession={session}
            accounts={accounts}
            isLoading={isAccountLoading}
            onSwitchAccount={onSwitchAccount}
            onRemoveAccount={onRemoveAccount}
            onAddAccount={onAddAccount}
          />
        </div>
      )}

      {/* Search and Filters */}
      <div className="p-3 border-b flex-shrink-0 flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Поиск чатов..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <ChatFilters
          filters={filters}
          onFiltersChange={setFilters}
        />
      </div>

      {/* Chat list */}
      <div
        className="flex-1 min-h-0 overflow-auto telegram-scrollbar-hidden"
        ref={scrollRef}
        onScroll={handleScroll}
      >
        {isLoading && chats.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Загрузка...
          </div>
        ) : (filteredChats.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
            <MessageCircle className="h-8 w-8 mb-2" />
            <p>{search ? 'Ничего не найдено' : 'Нет чатов'}</p>
          </div>
        ) : (
          <div className="divide-y">
            {filteredChats.map((chat) => (
              <ChatListItem
                key={`${chat.telegramChatId}-${chat.id}`}
                chat={chat}
                isSelected={selectedChatId === chat.telegramChatId}
                onClick={() => onSelectChat(chat)}
                formatTime={formatTime}
              />
            ))}

            {/* Loading indicator for more chats */}
            {isLoadingMore && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">Загрузка...</span>
              </div>
            )}

            {/* No more chats indicator */}
            {!hasMore && filteredChats.length > 0 && !search && (
              <div className="flex items-center justify-center py-4">
                <span className="text-xs text-muted-foreground">Все чаты загружены</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

interface ChatListItemProps {
  chat: TelegramChat;
  isSelected: boolean;
  onClick: () => void;
  formatTime: (date: Date | string | null | undefined) => string;
}

function ChatListItem({ chat, isSelected, onClick, formatTime }: ChatListItemProps) {
  const title = chat.title || 'Неизвестный чат';

  // Парсим последнее сообщение для определения типа медиа
  const { icon: MediaIcon, text: messageText } = parseLastMessage(chat.lastMessage);

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 p-3 text-left hover:bg-muted/50 transition-colors overflow-hidden',
        isSelected && 'bg-muted'
      )}
    >
      {/* Avatar */}
      <ChatAvatar chatId={chat.id} title={title} className="w-12 h-12 flex-shrink-0" />

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 min-w-0">
            <span className="font-medium truncate">{title}</span>
            {chat.linkedUserId && (
              <MastersClubIcon className="h-3.5 w-3.5 flex-shrink-0 text-primary" />
            )}
          </div>
          {chat.lastMessageDate && (
            <span className="text-xs text-muted-foreground flex-shrink-0">
              {formatTime(chat.lastMessageDate)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 text-sm text-muted-foreground min-w-0 overflow-hidden">
            {MediaIcon && <MediaIcon className="h-4 w-4 flex-shrink-0" />}
            <span className="truncate">{messageText}</span>
          </div>
          {chat.unreadCount > 0 && (
            <span className="flex-shrink-0 bg-primary text-primary-foreground text-xs rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
              {chat.unreadCount}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

/**
 * Парсит последнее сообщение и возвращает иконку и текст
 */
function parseLastMessage(lastMessage: string | null | undefined): {
  icon: typeof Image | null;
  text: string;
} {
  if (!lastMessage) {
    return { icon: null, text: 'Нет сообщений' };
  }

  // Паттерны для определения типа медиа (на основе getMediaLabel в бэкенде)
  const mediaPatterns: Array<{ pattern: RegExp; icon: typeof Image; label: string }> = [
    { pattern: /^📷\s*Фото$/i, icon: Image, label: 'Фото' },
    { pattern: /^🎬\s*Видео$/i, icon: Video, label: 'Видео' },
    { pattern: /^🔵\s*Видеосообщение$/i, icon: CircleDot, label: 'Видеосообщение' },
    { pattern: /^🎤\s*Голосовое сообщение$/i, icon: Mic, label: 'Голосовое сообщение' },
    { pattern: /^🎭?\s*Стикер$/i, icon: Sticker, label: 'Стикер' },
    { pattern: /Стикер$/i, icon: Sticker, label: 'Стикер' },
    { pattern: /^📄\s*Документ$/i, icon: FileIcon, label: 'Документ' },
    { pattern: /^📍\s*Геопозиция$/i, icon: MapPin, label: 'Геопозиция' },
    { pattern: /^👤\s*Контакт$/i, icon: User, label: 'Контакт' },
    { pattern: /^📊\s*Опрос$/i, icon: BarChart3, label: 'Опрос' },
    { pattern: /^🎵\s*Аудио$/i, icon: Music, label: 'Аудио' },
    { pattern: /^GIF$/i, icon: Video, label: 'GIF' },
  ];

  for (const { pattern, icon, label } of mediaPatterns) {
    if (pattern.test(lastMessage)) {
      return { icon, text: label };
    }
  }

  // Обычный текст
  return { icon: null, text: lastMessage };
}
